import { type JSX, useCallback, useEffect, useState } from 'react';

import { abhaken, tag as ladeTag, type Anwesenheit, type Empfangstermin } from '../api/empfang';
import { Maskenverweis } from '../komponenten/Maskenverweis';
import {
  korrigieren,
  neueKennung,
  staende as ladeStaende,
  stellvertretend,
  vorlage as ladeVorlage,
  type Checkinfeld,
  type Checkinstand,
  type Checkintext,
} from '../api/checkin';
import { Unterschriftfeld } from '../komponenten/Unterschriftfeld';
import { standorte as ladeStandorte, type Planungsstandort } from '../api/tagesplanung';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Sinnbild } from '../komponenten/Sinnbild';
import { Planung } from '../komponenten/Planung';
import { Rueckfrage } from '../komponenten/Rueckfrage';

/**
 * Der Empfang am Kurstag (TAG-73, BUC-53).
 *
 * ============================================================================
 * DIE ARBEITSLISTE, DIE TAG-73 VERLANGT
 * ============================================================================
 *
 * „Ist ein Teilnehmer beim Check-in noch keiner Gruppe zugeordnet, wird er
 * sichtbar an den Empfang verwiesen, statt eine leere oder vorläufige Angabe zu
 * erhalten. Der Empfang sieht diese Fälle in einer Arbeitsliste."
 *
 * Ein Platz ohne Namen (BUC-69) IST dieser Fall, und er ist häufig: Ein
 * Firmenkunde bucht fünf Plätze und reicht drei Namen nach. Am Morgen stehen
 * fünf Menschen da, zwei davon in keiner Liste.
 *
 * DESHALB STEHEN DIE OFFENEN PLÄTZE OBEN. Nicht als Fußnote unter der
 * Anwesenheitsliste: Sie sind die Arbeit, alles andere ist Abhaken.
 *
 * ============================================================================
 * WAS HIER NICHT STEHT
 * ============================================================================
 *
 * Kein Preis, kein Deckungsbeitrag, keine Buchungshistorie. Wer am Tresen
 * steht, braucht Name, Platz und Termin — der Rest ist die Buchungsmaske, und
 * der Weg dorthin ist die Buchungsnummer, die hier an jeder Zeile steht.
 *
 * ============================================================================
 * WARUM DAS NICHT IM TRAINERBEREICH STEHT
 * ============================================================================
 *
 * Hier stehen NAMEN. RES-31 in der Fassung von ÄA-20: „Teilnehmernamen
 * erscheinen in KEINER Ansicht des Trainerbereichs." TAG-42 nennt für die
 * mobile Trainersicht eine „Gruppenliste" — das ist ein Widerspruch im
 * Anforderungsbestand, und er gehört dem Auftraggeber und nicht dieser Maske.
 * Bis er entschieden ist, hakt der Empfang ab (Recht `tagesplan/checkin`).
 */

/** Heute, als `JJJJ-MM-TT` in Ortszeit (FRS-01). */
function heute(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function alsUhrzeit(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

export function Empfang({
  darfAbhaken,
  beiTerminakte,
}: {
  readonly darfAbhaken: boolean;
  /** CRM-117 (L6): Benennen und Erfassen am Exklusivtermin stehen in der Terminakte. */
  readonly beiTerminakte?: (termin: string) => void;
}): JSX.Element {
  const [standorte, setzeStandorte] = useState<readonly Planungsstandort[]>([]);
  const [standort, setzeStandort] = useState('');
  const [datum, setzeDatum] = useState(heute);
  const [suche, setzeSuche] = useState('');
  const [termine, setzeTermine] = useState<readonly Empfangstermin[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  /* TAG-62, TAG-64: der Check-in-Stand je Platz. */
  const [checkins, setzeCheckins] = useState<ReadonlyMap<string, Checkinstand>>(new Map());
  const [offen, setzeOffen] = useState<
    | { art: 'einchecken'; teilnehmer: string; name: string; texte: readonly Checkintext[]; felder: readonly Checkinfeld[] }
    | { art: 'korrigieren'; checkin: string; name: string }
    | null
  >(null);

  useEffect(() => {
    void (async () => {
      try {
        const s = await ladeStandorte();
        setzeStandorte(s.standorte);
        /* Wie in der Tagesplanung: der Hauptstandort, sonst der erste (0147). */
        const anfang =
          s.standorte.find((x) => x.hauptstandort)?.kennung ?? s.standorte[0]?.kennung ?? '';
        setzeStandort((jetzt) => (jetzt === '' ? anfang : jetzt));
      } catch (e) {
        setzeFehler(e instanceof ApiFehler ? e : null);
        if (!(e instanceof ApiFehler)) throw e;
      }
    })();
  }, []);

  const holen = useCallback(async () => {
    if (standort === '') return;
    setzeLaedt(true);
    try {
      const antwort = await ladeTag(standort, datum);
      setzeTermine(antwort.termine);
      const staende = await ladeStaende(antwort.termine.map((t) => t.termin));
      setzeCheckins(new Map(staende.map((c) => [c.teilnehmer, c])));
      setzeFehler(null);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    } finally {
      setzeLaedt(false);
    }
  }, [standort, datum]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /**
   * Abhaken und neu laden.
   *
   * NEU GELADEN UND NICHT ÖRTLICH GEÄNDERT: Am Tresen arbeiten zwei Leute
   * nebeneinander an derselben Liste. Wer seine eigene Eingabe fortschreibt,
   * zeigt danach einen Stand, den es auf dem Server nicht gibt.
   */
  async function setze(teilnehmer: string, wert: Anwesenheit, name: string): Promise<void> {
    setzeMeldung(null);
    try {
      await abhaken(teilnehmer, wert);
      await holen();
      setzeMeldung(
        wert === null
          ? `${name}: Haken zurückgenommen.`
          : `${name} ist ${wert === 'anwesend' ? 'anwesend' : 'abwesend'}.`,
      );
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    }
  }

  /** TAG-62: stellvertretend einchecken — erst die Vorlage laden, dann bestaetigen. */
  async function eincheckenOeffnen(teilnehmer: string, name: string): Promise<void> {
    setzeMeldung(null);
    try {
      const v = await ladeVorlage(teilnehmer);
      setzeOffen({ art: 'einchecken', teilnehmer, name, texte: v.texte, felder: v.felder });
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    }
  }

  const offene = termine.flatMap((t) =>
    t.plaetze.filter((p) => p.name === null).map((p) => ({ termin: t, platz: p })),
  );
  const gesamt = termine.reduce((s, t) => s + t.plaetze.length, 0);
  const angehakt = termine.reduce(
    (s, t) => s + t.plaetze.filter((p) => p.anwesenheit !== null).length,
    0,
  );

  /*
   * DIE SCHNELLE SUCHE AM TRESEN (ÄA-41, Auftrag #18): Wer vor einem steht,
   * nennt seinen Namen oder zeigt die Buchungsnummer. Gesucht wird in den
   * Plätzen des Tages, die schon geladen sind — ohne neue Abfrage.
   */
  const wort = suche.trim().toLocaleLowerCase('de-DE');
  const gezeigt =
    wort === ''
      ? termine
      : termine
          .map((t) => ({
            ...t,
            plaetze: t.plaetze.filter((p) =>
              [p.name, p.buchungsnummer, p.kundenname].some(
                (w) => w !== null && w.toLocaleLowerCase('de-DE').includes(wort),
              ),
            ),
          }))
          .filter((t) => t.plaetze.length > 0);

  const leer = laedt || termine.length === 0;

  /* Die Werkzeugleiste: Standort, Tag und die schnelle Suche. */
  const werkzeuge = (
    <>
      <label className="liste__filter">
        Standort{' '}
        <select
          aria-label="Standort"
          value={standort}
          onChange={(e) => {
            setzeStandort(e.target.value);
          }}
        >
          {standorte.map((s) => (
            <option key={s.kennung} value={s.kennung}>
              {s.bezeichnung}
            </option>
          ))}
        </select>
      </label>{' '}
      <label className="liste__filter">
        Tag{' '}
        <input
          type="date"
          aria-label="Tag"
          value={datum}
          onChange={(e) => {
            setzeDatum(e.target.value);
          }}
        />
      </label>
      <label className="filterleiste__suche">
        <Sinnbild name="magnifying-glass" />
        <input
          className="liste__suche"
          type="search"
          aria-label="Teilnehmer oder Buchung suchen"
          placeholder="Name oder Buchungsnummer"
          value={suche}
          onChange={(e) => {
            setzeSuche(e.target.value);
          }}
        />
      </label>
    </>
  );

  /* Die Seitenleiste: der Stand des Tages und der gewählte Platz. */
  const standDesTages = (
    <>
      {/*
        DIE OFFENEN PLÄTZE ZUERST (TAG-73). Sie sind der Grund für diese
        Maske: Wer morgens hereinkommt, soll in einer halben Sekunde
        sehen, ob heute etwas zu klären ist.
      */}
      <div className={offene.length === 0 ? 'streifen streifen--gut' : 'streifen streifen--warn'}>
        {offene.length === 0 ? (
          <>Zu jedem der {gesamt} Plätze ist ein Name gemeldet.</>
        ) : (
          <>
            <strong>
              {offene.length} von {gesamt} Plätzen ohne Namen
            </strong>{' '}
            — wer dazu am Tresen steht, wird hier eingetragen, bevor er auf den Platz geht.
            Die Namen trägt die Buchung; die Nummer steht an der Zeile.
          </>
        )}
      </div>

      <p className="hilfe">
        {angehakt} von {gesamt} Plätzen erfasst. Ein Platz ohne Haken ist{' '}
        <strong>offen</strong> und nicht abwesend — das ist eine andere Auskunft.
      </p>

      {offen?.art === 'einchecken' && (
        <Stellvertretung
          name={offen.name}
          texte={offen.texte}
          felder={offen.felder}
          beiAbbruch={() => {
            setzeOffen(null);
          }}
          beiAbschluss={async (abschluss) => {
            await stellvertretend(offen.teilnehmer, abschluss);
            setzeOffen(null);
            await holen();
            setzeMeldung(`${offen.name} ist eingecheckt — als Fremderfassung vermerkt.`);
          }}
        />
      )}
    </>
  );

  return (
    /*
     * SEITENTYP PLANUNG (ÄA-41): oben die Werkzeugleiste (Standort, Tag,
     * Suche), darunter die Termine des Tages in voller Breite, rechts, was
     * gerade zu tun ist — der Stand des Tages und der Platz, der gewählt ist.
     * Tresenarbeit auf dem Tablet: große Ziele, auch bei 1024 px.
     */
    <div className="empfang">
      <Planung
        werkzeuge={werkzeuge}
        seitenBeschriftung="Stand des Tages"
        {...(leer ? {} : { seite: standDesTages })}
        oben={
          <>
            {fehler !== null && <Meldung fehler={fehler} />}
            {meldung !== null && (
              <p className="hinweiszeile" role="status">
                {meldung}
              </p>
            )}
            {laedt ? (
              <p className="hinweiszeile">Wird geladen …</p>
            ) : termine.length === 0 ? (
              <p className="hinweiszeile">
                An diesem Tag steht an diesem Standort kein gebuchter Termin an.
              </p>
            ) : undefined}
          </>
        }
      >
        {!leer && (
          <>
            {gezeigt.length === 0 && (
              <p className="hinweiszeile">Kein Platz passt zu „{suche.trim()}“.</p>
            )}
            {gezeigt.map((t) => (
              <div key={t.termin} className="karte">
                <div className="karte__titel">
                  <h2>
                    {beiTerminakte === undefined ? (
                      <>
                        {alsUhrzeit(t.beginn)} · {t.produkt}
                      </>
                    ) : (
                      <Maskenverweis
                        maske={{ art: 'terminakte', id: t.termin }}
                        beiKlick={() => beiTerminakte(t.termin)}
                        title="Terminakte öffnen"
                      >
                        {alsUhrzeit(t.beginn)} · {t.produkt}
                      </Maskenverweis>
                    )}
                  </h2>
                  <small>
                    {t.artikelnummer} · {t.terminart === 'exklusiv' ? 'Exklusivtermin' : 'Einzelplatzverkauf'}{' '}
                    · {t.plaetze.length} Plätze
                  </small>
                </div>
                {/*
                  CRM-106: Am Tresen und unterwegs steht jeder Platz auf dem
                  Telefon als Karte — Name, Buchung und Stand untereinander, die
                  Knöpfe in voller Breite darunter.
                */}
                <div className="tabellenhuelle tabellenhuelle--telefon">
                  <table
                    className="liste__tabelle tabelle--karten"
                    aria-label={`Plätze ${alsUhrzeit(t.beginn)}`}
                  >
                    <thead>
                      <tr>
                        <th scope="col">Platz</th>
                        <th scope="col">Teilnehmer</th>
                        <th scope="col">Buchung</th>
                        <th scope="col">Stand</th>
                        {darfAbhaken && <th scope="col">Handlung</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {t.plaetze.map((p) => (
                        <tr key={p.teilnehmer} className={p.name === null ? 'liste__zeile--blass' : undefined}>
                          <td data-spalte="Platz">{p.platz}</td>
                          <td data-spalte="Teilnehmer">
                            {p.name ?? (
                              /*
                                OHNE ANFORDERUNGSNUMMER AM BILDSCHIRM (Befund B9).
                                Die Marke ist eine Angabe über diesen Platz, kein
                                Hinweistext — „(BUC-69)" daneben liest sich wie ein
                                Fehlercode. Die IDs stehen in den erklärenden
                                Sätzen und im Quelltext.
                              */
                              <span className="marke marke--warn">kein Name gemeldet</span>
                            )}
                          </td>
                          <td data-spalte="Buchung">
                            <span className="liste__nummer">{p.buchungsnummer}</span>
                            {p.kundenname !== null && <span className="hilfe"> · {p.kundenname}</span>}
                          </td>
                          <td data-spalte="Stand">
                            {checkins.get(p.teilnehmer) !== undefined && (
                              <>
                                <span className="marke marke--gut">
                                  eingecheckt {alsUhrzeit(checkins.get(p.teilnehmer)!.abgeschlossenAm)}
                                </span>{' '}
                                <span className="hilfe">
                                  {checkins.get(p.teilnehmer)!.art === 'selbst'
                                    ? 'am Tablet'
                                    : `durch ${checkins.get(p.teilnehmer)!.erfasstVon ?? 'Empfang'}`}
                                </span>
                                <br />
                              </>
                            )}
                            {p.anwesenheit === 'anwesend' && <span className="marke marke--gut">anwesend</span>}
                            {p.anwesenheit === 'abwesend' && <span className="marke marke--krit">abwesend</span>}
                            {p.anwesenheit === null && <span className="hilfe">offen</span>}
                          </td>
                          {darfAbhaken && checkins.get(p.teilnehmer) !== undefined && (
                            <td>
                              {/* TAG-65: Ein Check-in wird korrigiert, nicht weggehakt. */}
                              <button
                                type="button"
                                className="knopf knopf--eingriff knopf--klein"
                                onClick={() => {
                                  setzeOffen({
                                    art: 'korrigieren',
                                    checkin: checkins.get(p.teilnehmer)!.checkin,
                                    name: p.name ?? '',
                                  });
                                }}
                              >
                                Check-in korrigieren …
                              </button>
                            </td>
                          )}
                          {darfAbhaken && checkins.get(p.teilnehmer) === undefined && (
                            <td>
                              <button
                                type="button"
                                className="knopf knopf--klein knopf--haupt"
                                disabled={p.name === null}
                                onClick={() => void eincheckenOeffnen(p.teilnehmer, p.name ?? '')}
                              >
                                Einchecken …
                              </button>{' '}
                              {/*
                                DREI ZUSTÄNDE, ZWEI KNÖPFE — und der Weg zurück ist
                                derselbe Knopf. Wer den falschen Platz angehakt hat,
                                drückt noch einmal, und es steht wieder „offen".
                                „Abwesend" wäre dafür falsch: Das ist eine
                                Feststellung und kein Zurücknehmen.
                              */}
                              <button
                                type="button"
                                className={p.anwesenheit === 'anwesend' ? 'knopf knopf--klein' : 'knopf knopf--klein knopf--haupt'}
                                disabled={p.name === null}
                                onClick={() =>
                                  void setze(
                                    p.teilnehmer,
                                    p.anwesenheit === 'anwesend' ? null : 'anwesend',
                                    p.name ?? `Platz ${String(p.platz)}`,
                                  )
                                }
                              >
                                {p.anwesenheit === 'anwesend' ? 'Haken zurück' : 'Da'}
                              </button>{' '}
                              <button
                                type="button"
                                className="knopf knopf--klein"
                                disabled={p.name === null}
                                onClick={() =>
                                  void setze(
                                    p.teilnehmer,
                                    p.anwesenheit === 'abwesend' ? null : 'abwesend',
                                    p.name ?? `Platz ${String(p.platz)}`,
                                  )
                                }
                              >
                                {p.anwesenheit === 'abwesend' ? 'Haken zurück' : 'Fehlt'}
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </>
        )}

        {/*
          TAG-65: die Korrektur mit Grund, als Rückfrage im Pop-up (CRM-104).
          Der Grund steht nur hier; der Nachweis bleibt stehen, als korrigiert.
        */}
        {offen?.art === 'korrigieren' && (
          <Rueckfrage
            frage="Check-in korrigieren?"
            unterzeile={offen.name}
            tat="Korrigieren"
            grund={{
              beschriftung: 'Grund der Korrektur',
              hilfe: 'Mindestens drei Zeichen; der Grund steht im Protokoll.',
            }}
            beiBestaetigen={async (grund) => {
              await korrigieren(offen.checkin, grund);
              setzeOffen(null);
              await holen();
              setzeMeldung(`Check-in von ${offen.name} korrigiert. Der Platz ist wieder offen.`);
            }}
            beiSchliessen={() => {
              setzeOffen(null);
            }}
          >
            <p>
              Der Nachweis bleibt erhalten und wird als korrigiert vermerkt. Der Platz steht danach
              wieder offen.
            </p>
          </Rueckfrage>
        )}
      </Planung>
    </div>
  );
}

/**
 * TAG-62: der Check-in am Tresen, stellvertretend.
 *
 * Dieselben Texte und Angaben wie am Tablet. Die Unterschrift ist freiwillig —
 * der Empfang steht mit seinem Namen dafuer, dass der Teilnehmer die Texte zur
 * Kenntnis genommen hat, und das Protokoll haelt fest, wer es war.
 */
export function Stellvertretung({
  name,
  texte,
  felder,
  beiAbschluss,
  beiAbbruch,
}: {
  readonly name: string;
  readonly texte: readonly Checkintext[];
  readonly felder: readonly Checkinfeld[];
  readonly beiAbschluss: (abschluss: {
    id: string;
    texte: readonly string[];
    unterschrift: string | null;
    angaben: Record<string, string>;
    abgeschlossenAm: string;
  }) => Promise<void>;
  readonly beiAbbruch: () => void;
}): JSX.Element {
  const [id] = useState(neueKennung);
  const [bestaetigt, setzeBestaetigt] = useState(false);
  const [angaben, setzeAngaben] = useState<Record<string, string>>({});
  const [unterschrift, setzeUnterschrift] = useState<string | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);

  return (
    <div className="karte" aria-label={`${name} einchecken`}>
      <h2>{name} einchecken</h2>
      <p className="hilfe">
        Legen Sie die Texte vor: {texte.map((t) => `${t.artbezeichnung} (${t.kennung}, Version ${String(t.version)})`).join(', ')}.
      </p>
      <label className="checkin__haken">
        <input
          type="checkbox"
          checked={bestaetigt}
          onChange={() => {
            setzeBestaetigt(!bestaetigt);
          }}
        />
        Der Teilnehmer hat die Texte zur Kenntnis genommen und bestätigt.
      </label>
      {felder.map((f) => (
        <label key={f.schluessel} className="feld">
          <span className="feld__beschriftung">
            {f.bezeichnung}
            {f.pflicht ? ' *' : ''}
          </span>
          <input
            value={angaben[f.schluessel] ?? ''}
            onChange={(e) => {
              setzeAngaben((alt) => ({ ...alt, [f.schluessel]: e.target.value }));
            }}
          />
        </label>
      ))}
      <Unterschriftfeld beiAenderung={setzeUnterschrift} beschriftung="Unterschrift (freiwillig)" />
      {fehler !== null && <Meldung fehler={fehler} />}
      <div className="knopfreihe">
        <button type="button" className="knopf knopf--klein" onClick={beiAbbruch}>
          Abbrechen
        </button>
        <button
          type="button"
          className="knopf knopf--haupt knopf--klein"
          disabled={!bestaetigt}
          onClick={() => {
            setzeFehler(null);
            beiAbschluss({
              id,
              texte: texte.map((t) => t.id),
              unterschrift,
              angaben,
              abgeschlossenAm: new Date().toISOString(),
            }).catch((e: unknown) => {
              setzeFehler(e instanceof ApiFehler ? e : null);
            });
          }}
        >
          Einchecken
        </button>
      </div>
    </div>
  );
}
